// src/lib/fragoEngine.js
//
// Phase 4 of the FRAGO Rework — the runtime that progresses phases /
// objectives and fires activation triggers + resolution actions.
//
// Three public entry points:
//
//   checkFragoObjectives(scanType, entityId, eventId, userId)
//     Called from the QR scanner after a primary scan action lands
//     (equipment / checkpoint / player / kill). Flips matching active
//     objectives to completed, cascades phase + FRAGO state, fires
//     resolution. Returns a summary the scanner can render in the result
//     panel ("OBJECTIVE COMPLETE — phase advanced").
//
//   activateFragosByEntityScan(triggerType, entityId, eventId, userId)
//     Sibling call — checks for queued FRAGOs whose activation_trigger_type
//     matches the scan and flips them to active. Run alongside the
//     objective check; an entity can both complete an objective AND
//     trigger a new FRAGO in the same scan.
//
//   findPlayerScanObjective(scannedUserId, scannerUserId, eventId)
//     Amendment #1 priority gate — when a kill scan happens, the scanner
//     calls this first. If the scanned player matches an active
//     player_scan objective for the scanner, the kill flow is skipped and
//     we run the player_scan completion path instead. "You're there to
//     find them, not kill them."
//
// All writes go through the shared supabase client and rely on the
// frago_objectives_player_complete RLS policy added in the Phase 1
// migration — the policy is permissive (auth.role()='authenticated')
// because gating happens in this engine before the UPDATE fires.

import { supabase } from './supabaseClient'

// Map QR scan types to the matching frago_objectives.objective_type
// strings. Kept as a function so callers can extend the mapping (e.g.,
// future "interact" scans) without touching the engine internals.
const SCAN_TYPE_TO_OBJECTIVE_TYPE = {
  equipment: 'equipment_scan',
  checkpoint: 'checkpoint_scan',
  player: 'player_scan',
  kill: 'kill_target',
}

// Amendment #1: a kill-flow scan of a player who has an active
// player_scan objective targeting them (for the scanning user) should
// resolve as a CONTACT, not a kill. The scanner calls this first.
// Returns the matched objective (with phase metadata) or null.
export async function findPlayerScanObjective(scannedUserId, scannerUserId, eventId) {
  if (!scannedUserId || !scannerUserId || !eventId) return null
  // Pull active player_scan objectives targeting this player; we filter
  // by phase/FRAGO event scope client-side because the schema doesn't
  // carry event_id on objectives directly.
  const { data: objs } = await supabase
    .from('frago_objectives')
    .select('id, phase_id, title, target_id, phase:frago_phases!frago_objectives_phase_id_fkey(id, frago_id, status, frago:bounties!frago_phases_frago_id_fkey(id, event_id, status))')
    .eq('objective_type', 'player_scan')
    .eq('target_id', scannedUserId)
    .eq('status', 'active')
  const scoped = (objs || []).filter(o => o.phase?.frago?.event_id === eventId && o.phase?.frago?.status === 'active')
  if (scoped.length === 0) return null
  // Pick the first match. Multiple simultaneous player_scan objectives
  // on the same player are unusual but possible; downstream resolution
  // will fire for the first match and the caller can re-scan to pick up
  // any remaining objectives if needed.
  return scoped[0]
}

// Core entry point: a primary scan landed, see if any active objectives
// match and progress the FRAGO. Returns { completed, phaseAdvanced,
// fragoCompleted, matchedTitle } so callers can show feedback.
export async function checkFragoObjectives(scanType, entityId, eventId, userId) {
  const objectiveType = SCAN_TYPE_TO_OBJECTIVE_TYPE[scanType]
  if (!objectiveType || !entityId || !eventId) {
    return { completed: false }
  }
  // Find an active objective for this entity, scoped to the event. The
  // status='active' filter is in the partial index from migration
  // 20260512100000 so this query is cheap even with hundreds of objectives.
  const { data: objs } = await supabase
    .from('frago_objectives')
    .select('id, phase_id, title, concurrent, phase:frago_phases!frago_objectives_phase_id_fkey(id, frago_id, status, gate_phase_id, frago:bounties!frago_phases_frago_id_fkey(id, event_id, status, resolution_on_success))')
    .eq('objective_type', objectiveType)
    .eq('target_id', entityId)
    .eq('status', 'active')
  const match = (objs || []).find(o => o.phase?.frago?.event_id === eventId && o.phase?.frago?.status === 'active')
  if (!match) return { completed: false }

  // FIX 9 — delegated FRAGO scope. If the FRAGO has any
  // frago_assignments rows, the scanner must be in the delegate node
  // tree (member, leader, or descendant) to complete the objective.
  // Broadcast FRAGOs (no assignments) skip the check via the helper.
  // Silent skip on false — surfacing "not your mission" would leak
  // existence of the delegated FRAGO to non-delegates.
  try {
    const { data: canComplete } = await supabase.rpc('_user_can_complete_frago', {
      p_frago_id: match.phase.frago.id,
      p_user_id: userId,
    })
    if (canComplete === false) return { completed: false }
  } catch (e) {
    console.warn('[fragoEngine] _user_can_complete_frago failed', e)
  }

  // Flip the matched objective to completed.
  await supabase.from('frago_objectives').update({
    status: 'completed',
    completed_by: userId,
    completed_at: new Date().toISOString(),
  }).eq('id', match.id)

  // FRAGO restructure — fire the per-objective notify_config and
  // opposing_effect side effects. The RPC handles audience scoping
  // (commander / faction / all + opposing faction) and the optional
  // counter-FRAGO chain. Wrapped so a push failure doesn't break the
  // phase/FRAGO cascade below.
  try {
    await supabase.rpc('fire_frago_objective_completed', { p_objective_id: match.id })
  } catch (e) {
    console.warn('[fragoEngine] fire_frago_objective_completed failed', e)
  }
  // FIX 3 — sequential objectives within a phase. Promote the next
  // group (or single non-concurrent) only after this one resolves.
  // Idempotent: if the phase already advanced (no locked left), the
  // RPC is a no-op. Runs BEFORE the phase-advance check below so
  // the new "active" state is reflected when we decide whether the
  // phase is done.
  try {
    await supabase.rpc('_activate_next_objective_group', { p_phase_id: match.phase.id })
  } catch (e) {
    console.warn('[fragoEngine] _activate_next_objective_group failed', e)
  }

  // Decide if the phase advances. A phase completes when every
  // NON-concurrent objective is done (concurrent objectives flag
  // background tasks like "defend the controller" that don't block phase
  // progression — they need to satisfy independently or the phase still
  // ends, just with a partial-success marker).
  const phaseId = match.phase.id
  const fragoId = match.phase.frago.id
  const { data: siblings } = await supabase
    .from('frago_objectives')
    .select('id, status, concurrent')
    .eq('phase_id', phaseId)
  const blockingDone = (siblings || []).filter(s => !s.concurrent).every(s => s.status === 'completed' || s.status === 'failed')

  let phaseAdvanced = false
  let fragoCompleted = false
  if (blockingDone) {
    await supabase.from('frago_phases')
      .update({ status: 'completed' })
      .eq('id', phaseId)
    // Unlock the gated next phase, if there is one. We look up phases
    // whose gate_phase_id == phaseId — the FragoBuilder writes a strict
    // linear chain so this resolves to 0 or 1 rows in practice, but the
    // query supports fan-out if we ever add multi-gated phases.
    const { data: gated } = await supabase
      .from('frago_phases')
      .select('id')
      .eq('gate_phase_id', phaseId)
      .eq('status', 'locked')
    for (const next of (gated || [])) {
      // Timer engine: stamp activated_at when the phase flips active
      // so the timed-objective RPC has a real reference point.
      await supabase.from('frago_phases').update({ status: 'active', activated_at: new Date().toISOString() }).eq('id', next.id)
      // FIX 3 — only the first objective group activates here. The
      // RPC walks the phase's objectives in order, takes the lowest-
      // numbered locked one (or the consecutive concurrent block
      // starting from it), and flips just that group active.
      // Everything else stays locked until the active group resolves.
      try {
        await supabase.rpc('_activate_next_objective_group', { p_phase_id: next.id })
      } catch (e) {
        console.warn('[fragoEngine] _activate_next_objective_group on phase advance failed', e)
      }
      phaseAdvanced = true
    }
    // If no gated next phase, this might be the final phase. Check.
    if (!phaseAdvanced) {
      const { data: openPhases } = await supabase
        .from('frago_phases')
        .select('id, status')
        .eq('frago_id', fragoId)
      const allDone = (openPhases || []).every(p => p.status === 'completed' || p.status === 'failed')
      if (allDone) {
        await supabase.from('bounties').update({
          status: 'claimed',
          phase: 'resolved',
          claimed_by: userId,
          claimed_at: new Date().toISOString(),
        }).eq('id', fragoId)
        await executeFragoResolution(fragoId, 'success')
        fragoCompleted = true
      }
    }
  }

  return {
    completed: true,
    matchedTitle: match.title,
    phaseAdvanced,
    fragoCompleted,
  }
}

// Sibling: check for queued FRAGOs whose activation_trigger_type matches
// this scan and flip them to active. Called alongside checkFragoObjectives
// so a single scan can both complete an objective AND activate a new FRAGO
// (e.g., scanning the laptop completes an old recon mission and triggers
// the follow-up assault FRAGO).
export async function activateFragosByEntityScan(triggerType, entityId, eventId, userId) {
  // triggerType is one of 'player_scan', 'equipment_scan', 'checkpoint_scan'.
  if (!triggerType || !entityId || !eventId) return { activated: [] }
  // Bug 63 — activation_message needed by the scanner UI to show the
  // "INTEL DISCOVERED" line to the player who triggered the FRAGO.
  const { data: candidates } = await supabase
    .from('bounties')
    .select('id, mission_name, activation_trigger_config, commander_notify, activation_message, target_faction_id')
    .eq('event_id', eventId)
    .eq('category', 'frago')
    .eq('status', 'queued')
    .eq('activation_trigger_type', triggerType)
  const matched = (candidates || []).filter(b => b.activation_trigger_config?.target_id === entityId)
  const activated = []
  for (const frago of matched) {
    await activateFrago(frago.id, userId)
    activated.push(frago)
  }
  return { activated }
}

// Kill-event activation + defend-player failure check. Run from the kill
// flow after kill_log INSERT lands.
export async function onKillRecorded({ killerId, targetId, eventId }) {
  if (!eventId) return { activated: [], failed: [] }
  const activated = []
  const failed = []

  // Activation: any queued FRAGO with trigger_type='kill_event' AND
  // config.target_id = targetId fires now.
  const { data: killTriggered } = await supabase
    .from('bounties')
    .select('id, mission_name, activation_trigger_config, activation_message, commander_notify, target_faction_id')
    .eq('event_id', eventId)
    .eq('category', 'frago')
    .eq('status', 'queued')
    .eq('activation_trigger_type', 'kill_event')
  for (const frago of (killTriggered || []).filter(f => f.activation_trigger_config?.target_id === targetId)) {
    await activateFrago(frago.id, killerId)
    activated.push(frago)
  }

  // Kill-target objective completion: if the killed player matches an
  // active kill_target objective, flip it through the same path the
  // equipment/checkpoint flow uses.
  await checkFragoObjectives('kill', targetId, eventId, killerId)

  // Defend-player FAILURE: any active defend_player objective targeting
  // the killed player fails NOW. Cascade to the phase + FRAGO if there
  // are no other in-flight objectives that could redeem the phase.
  const { data: defendObjs } = await supabase
    .from('frago_objectives')
    .select('id, phase_id, title, phase:frago_phases!frago_objectives_phase_id_fkey(id, frago_id, status, frago:bounties!frago_phases_frago_id_fkey(id, event_id, status))')
    .eq('objective_type', 'defend_player')
    .eq('target_id', targetId)
    .eq('status', 'active')
  for (const obj of (defendObjs || []).filter(o => o.phase?.frago?.event_id === eventId && o.phase?.frago?.status === 'active')) {
    await supabase.from('frago_objectives').update({ status: 'failed' }).eq('id', obj.id)
    await supabase.from('frago_phases').update({ status: 'failed' }).eq('id', obj.phase.id)
    await supabase.from('bounties').update({ status: 'failed', phase: 'failed' }).eq('id', obj.phase.frago.id)
    await executeFragoResolution(obj.phase.frago.id, 'fail')
    failed.push(obj.phase.frago.id)
  }

  return { activated, failed }
}

// Flip a queued FRAGO to active and unlock its first phase (the one with
// no gate_phase_id). Idempotent — re-running on an already-active FRAGO
// is a no-op.
//
// ANU-40: the bounties and frago_phases tables have GM-tier RLS on
// UPDATE, so the old client-side activation silently failed RLS when a
// regular player triggered the scan. The work now lives inside the
// SECURITY DEFINER activate_frago RPC; this wrapper exists so call
// sites don't have to change shape.
export async function activateFrago(fragoId, actingUserId) {
  const { data, error } = await supabase.rpc('activate_frago', {
    p_frago_id: fragoId,
    p_acting_user_id: actingUserId || null,
  })
  if (error) console.warn('[fragoEngine] activate_frago RPC failed', error)
  return data || null
}

// Apply the resolution_on_{success|fail|timeout} JSONB. Idempotent guard:
// the caller is responsible for only invoking once per outcome — we flip
// the FRAGO's status first so a double-call won't re-award points.
export async function executeFragoResolution(fragoId, outcome) {
  const { data: rows } = await supabase
    .from('bounties')
    .select('id, event_id, points, resolution_on_success, resolution_on_fail, resolution_on_timeout, target_faction_id')
    .eq('id', fragoId)
    .limit(1)
  const frago = rows?.[0]
  if (!frago) return
  const key = outcome === 'success' ? 'resolution_on_success'
            : outcome === 'fail'    ? 'resolution_on_fail'
            : 'resolution_on_timeout'
  const config = frago[key]
  if (!config) return

  // Activate a linked bounty if specified. We just flip status; the
  // bounty's own RLS / lifecycle handles downstream consequences.
  if (config.activate_bounty_id) {
    await supabase.from('bounties').update({
      status: 'active', phase: 'active',
    }).eq('id', config.activate_bounty_id).eq('status', 'queued')
  }

  // Trigger a linked FRAGO via the same activation path used by
  // entity-scan + kill_event. Recursive resolution chains terminate
  // naturally because activateFrago is a no-op on non-queued FRAGOs.
  if (config.trigger_frago_id) {
    await activateFrago(config.trigger_frago_id, null)
  }

  // Release intel message → mission_events log + future faction push.
  if (config.message) {
    await supabase.from('mission_events').insert({
      bounty_id: fragoId,
      event_type: outcome === 'success' ? 'frago_completed' : outcome === 'fail' ? 'frago_failed' : 'frago_expired',
      triggered_by: null,
      notes: config.message,
    }).then(() => {}).catch(() => {})
  }

  // Points: the stats views already award the FRAGO's `points` column to
  // the claimer on success; the resolution config's `points` is an extra
  // amount applied at the faction level. Stub for now — Phase 5 wires it
  // into the faction scoreboard.
  // TODO Phase 5: faction-level points adjustment from config.points.
}

// Escort-player checkpoint completion. The checkpoint scan handler
// already calls checkFragoObjectives('checkpoint', ...) which matches
// objective_type='checkpoint_scan' by target_id. Escort objectives use
// destination_id instead, so they need a sibling lookup. Match
// criteria:
//   - objective_type = 'escort_player'
//   - destination_id = the scanned checkpoint
//   - status = 'active'
//   - target_id has no pending/confirmed kill_log entry since the
//     phase activated (target alive at scan time)
//
// Run from the checkpoint scan handler immediately after
// checkFragoObjectives — same cascade logic via the engine's normal
// completion path.
export async function checkEscortObjectives(checkpointId, eventId, userId) {
  if (!checkpointId || !eventId) return { completed: false }
  const { data: objs } = await supabase
    .from('frago_objectives')
    .select('id, phase_id, title, target_id, concurrent, phase:frago_phases!frago_objectives_phase_id_fkey(id, frago_id, status, activated_at, gate_phase_id, frago:bounties!frago_phases_frago_id_fkey(id, event_id, status, resolution_on_success))')
    .eq('objective_type', 'escort_player')
    .eq('destination_id', checkpointId)
    .eq('status', 'active')
  const candidates = (objs || []).filter(o => o.phase?.frago?.event_id === eventId && o.phase?.frago?.status === 'active')
  for (const obj of candidates) {
    // FIX 9 — delegate scope on escort completion too.
    try {
      const { data: canComplete } = await supabase.rpc('_user_can_complete_frago', {
        p_frago_id: obj.phase.frago.id,
        p_user_id: userId,
      })
      if (canComplete === false) continue
    } catch (e) { console.warn('[fragoEngine] _user_can_complete_frago (escort) failed', e) }
    // Target survival check: any pending/confirmed kill_log entry
    // against the escort target since the phase activated invalidates
    // the escort completion (target died before arriving).
    const { data: deaths } = await supabase
      .from('kill_log')
      .select('id')
      .eq('target_id', obj.target_id)
      .in('status', ['pending','confirmed'])
      .gte('scanned_at', obj.phase.activated_at || '1970-01-01')
      .limit(1)
    if (deaths?.[0]) continue   // target died — escort fails on its own death path
    // Flip + cascade via the engine. Reusing checkFragoObjectives by
    // forcing target_id wouldn't work (the engine's match query is
    // type+target_id, not type+destination_id), so the cascade runs
    // inline here and mirrors the scan-completion path.
    await supabase.from('frago_objectives').update({
      status: 'completed', completed_by: userId, completed_at: new Date().toISOString(),
    }).eq('id', obj.id)
    // FIX 3 — promote next objective group in the same phase before
    // checking phase advance. Mirrors the checkFragoObjectives path.
    try {
      await supabase.rpc('fire_frago_objective_completed', { p_objective_id: obj.id })
    } catch (e) { console.warn('[fragoEngine] fire_frago_objective_completed (escort) failed', e) }
    try {
      await supabase.rpc('_activate_next_objective_group', { p_phase_id: obj.phase.id })
    } catch (e) { console.warn('[fragoEngine] _activate_next_objective_group (escort) failed', e) }
    const { data: siblings } = await supabase
      .from('frago_objectives').select('id, status, concurrent').eq('phase_id', obj.phase.id)
    const blockingDone = (siblings || []).filter(s => !s.concurrent).every(s => s.status === 'completed' || s.status === 'failed')
    if (blockingDone) {
      await supabase.from('frago_phases').update({ status: 'completed' }).eq('id', obj.phase.id)
      // Unlock + stamp activated_at on next phase.
      const { data: gated } = await supabase.from('frago_phases')
        .select('id').eq('gate_phase_id', obj.phase.id).eq('status', 'locked')
      for (const next of (gated || [])) {
        await supabase.from('frago_phases').update({ status: 'active', activated_at: new Date().toISOString() }).eq('id', next.id)
        // FIX 3 — first objective group only.
        try {
          await supabase.rpc('_activate_next_objective_group', { p_phase_id: next.id })
        } catch (e) { console.warn('[fragoEngine] _activate_next_objective_group (escort phase advance) failed', e) }
      }
      // FRAGO completion check.
      const { data: openPhases } = await supabase.from('frago_phases')
        .select('id, status').eq('frago_id', obj.phase.frago.id)
      const allDone = (openPhases || []).every(p => p.status === 'completed' || p.status === 'failed')
      if (allDone) {
        await supabase.from('bounties').update({
          status: 'claimed', phase: 'resolved',
          claimed_by: userId, claimed_at: new Date().toISOString(),
        }).eq('id', obj.phase.frago.id)
        await executeFragoResolution(obj.phase.frago.id, 'success')
      }
    }
    return { completed: true, matchedTitle: obj.title }
  }
  return { completed: false }
}

// ANU-168 — FRAGO fail condition check.
// When a scan occurs and the scanned entity matches an active objective
// with is_fail_condition=true, and the scanner's faction meets the
// fail_faction_scope, the parent FRAGO immediately fails.
// Returns { failed: true, fragoId, missionName, objectiveTitle } or
// { failed: false }.
// Call BEFORE checkFragoObjectives so a fail-condition scan short-circuits
// the normal objective-completion path.
export async function checkFragoFailConditions(scanType, entityId, eventId, userId) {
  const objectiveType = SCAN_TYPE_TO_OBJECTIVE_TYPE[scanType]
  if (!objectiveType || !entityId || !eventId || !userId) return { failed: false }

  const { data: objs } = await supabase
    .from('frago_objectives')
    .select('id, phase_id, title, fail_faction_scope, phase:frago_phases!frago_objectives_phase_id_fkey(id, frago_id, status, frago:bounties!frago_phases_frago_id_fkey(id, event_id, status, target_faction_id, mission_name))')
    .eq('objective_type', objectiveType)
    .eq('target_id', entityId)
    .eq('status', 'active')
    .eq('is_fail_condition', true)

  const candidates = (objs || []).filter(
    o => o.phase?.frago?.event_id === eventId && o.phase?.frago?.status === 'active'
  )
  if (candidates.length === 0) return { failed: false }

  // Get the scanner's faction in this event.
  const { data: epRows } = await supabase
    .from('event_players')
    .select('faction_id')
    .eq('user_id', userId)
    .eq('event_id', eventId)
    .limit(1)
  const scannerFactionId = epRows?.[0]?.faction_id || null

  for (const obj of candidates) {
    const frago = obj.phase?.frago
    if (!frago) continue

    const scope = obj.fail_faction_scope || 'opposing'
    // 'opposing' = scanner is NOT in the FRAGO's target faction.
    // 'any'      = any faction's scan triggers the fail.
    const isOpposing = frago.target_faction_id
      ? scannerFactionId !== frago.target_faction_id
      : true  // no target faction = broadcast FRAGO, treat all as opposing
    if (scope !== 'any' && !isOpposing) continue

    // Fail the objective, phase, and FRAGO.
    const now = new Date().toISOString()
    await supabase.from('frago_objectives')
      .update({ status: 'failed', completed_at: now, completed_by: userId })
      .eq('id', obj.id)
    await supabase.from('frago_phases')
      .update({ status: 'failed' })
      .eq('id', obj.phase.id)
    await supabase.from('bounties')
      .update({ status: 'failed', phase: 'failed' })
      .eq('id', frago.id)

    await executeFragoResolution(frago.id, 'fail')

    try {
      await supabase.from('mission_events').insert({
        bounty_id: frago.id,
        event_type: 'frago_failed',
        triggered_by: userId,
        notes: `Fail condition triggered: ${obj.title}`,
      })
    } catch { /* non-blocking */ }

    return {
      failed: true,
      fragoId: frago.id,
      missionName: frago.mission_name,
      objectiveTitle: obj.title,
    }
  }

  return { failed: false }
}

// Client-side wrapper around the complete_timed_objective RPC.
// FragoPhaseStepper fires this when a countdown hits zero. The RPC
// performs the server-side elapsed-duration check (anti-spoof) and the
// defend_player survival check, marks the objective complete, and
// cascades phase + FRAGO state. On the FRAGO-completion outcome the
// client fires executeFragoResolution(success) so points / linked
// bounty / chain FRAGO / intel message fire from the player's auth
// context — matches the scan-based completion path.
export async function completeTimedObjective(objectiveId) {
  const { data, error } = await supabase.rpc('complete_timed_objective', { p_objective_id: objectiveId })
  if (error) return { ok: false, error }
  if (data?.ok && data?.frago_completed && data?.frago_id) {
    await executeFragoResolution(data.frago_id, 'success')
  }
  return { ok: !!data?.ok, ...data }
}

// QR-code-triggered FRAGO activation (ANUBIS-FRAGO-{frago_id}). The QR
// router (Phase 6) hands the code here; we look up the trigger row, mark
// it scanned, and activate the linked FRAGO.
export async function activateFragoByQrCode(qrCode, scannerUserId, eventId) {
  const { data: rows } = await supabase
    .from('frago_trigger_qr')
    .select('id, frago_id, scanned, event_id')
    .eq('qr_code', qrCode)
    .limit(1)
  const trigger = rows?.[0]
  if (!trigger) return { ok: false, reason: 'unknown_qr' }
  if (trigger.event_id !== eventId) return { ok: false, reason: 'wrong_event' }
  if (trigger.scanned) return { ok: false, reason: 'already_scanned' }

  await supabase.from('frago_trigger_qr').update({
    scanned: true,
    scanned_by: scannerUserId,
    scanned_at: new Date().toISOString(),
  }).eq('id', trigger.id)

  const rpcResult = await activateFrago(trigger.frago_id, scannerUserId)
  return { ok: true, fragoId: trigger.frago_id, missionName: rpcResult?.mission_name || null }
}
